import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  PLATFORM_ID,
  signal
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CustomDatePipe,
  ResponseHttpSuccess,
  CustomMessageService,
  UtilService,
  LocalStorageService
} from 'library';
import { CategoryRequestService } from 'projects/store/src/app/services/api/v1/category-request.service';
import { environment } from 'projects/store/src/environments/environment';
import { RequestService } from 'projects/store/src/app/services/api/v1/request.service';
import { LocalStorageKey, SalesWorkflow } from 'projects/store/src/app/enums';
import { PixelService } from 'projects/store/src/app/services/api/v1/pixel.service';

export enum SimulatorSteps {
  'simulation' = 'simulation',
  'results' = 'results',
  'form' = 'form',
}

/**
 * Componente principal del Simulador Kredicash V3
 * Contiene toda la lógica común para V2 y V3
 * Actúa como container que renderiza la versión correcta según el flujo
 */
@Component({
  selector: 'app-sales-simulator-kredicash-v3',
  templateUrl: './sales-simulator-kredicash-v3.component.html',
})
export class SalesSimulatorKredicashComponentV3 implements OnInit {
  // Signals para estado reactivo
  products = signal<any>([]);
  minPrice = signal<number>(1000000000);
  maxPrice = signal<number>(0);
  minDay = signal<number>(15);
  maxDay = signal<number>(0);
  prices = signal<any>([]);
  isSubmitted = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  simulationsResults = signal<any>({});

  // Propiedades del simulador
  dayOptions: any[] = [];
  selectedProduct: any;
  selectedPrice!: number;
  selectedDay!: number;
  country = environment.global.country;
  form!: FormGroup;
  step = SimulatorSteps.simulation;
  flowCode: string;

  @Output() nextStepEvent = new EventEmitter<void>();

  constructor(
    protected categoryRequestService: CategoryRequestService,
    protected utilService: UtilService,
    protected customMessageService: CustomMessageService,
    protected requestService: RequestService,
    protected localStorageService: LocalStorageService,
    protected metaPixelService: PixelService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) protected platformId: Object
  ) {
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.initForm();
  }

  /**
   * Determina si el flujo actual es Krdya (V3)
   */
  get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-kredicash'];
  }

  /**
   * Inicializa el formulario reactivo
   */
  protected initForm(): void {
    this.form = new FormGroup({
      amount: new FormControl(''),
      quota_quantity: new FormControl(''),
      salary: new FormControl(null, Validators.required),
      frecuency: new FormControl('')
    });
  }

  ngOnInit(): void {
    this.requestService.step.set(1);
    this.load();
  }

  /**
   * Carga los productos por categoría desde el backend
   */
  protected load(): void {
    this.utilService.enablePageLoading();
    this.localStorageService.remove(LocalStorageKey.simulation);

    this.categoryRequestService.getProductsByCategory(this.requestService.categoryId).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.products.set(resp.body.data.list);
          this.onExtractMinMaxPrices();
        } else {
          this.customMessageService.responseError();
          this.utilService.disablePageLoading();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.comunicationError();
        this.utilService.disablePageLoading();
      }
    });
  }

  /**
   * Extrae los precios mínimos y máximos de los productos
   */
  onExtractMinMaxPrices(): void {
    let availableDays: number[] = [];

    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        this.minPrice.set(row1.prices.suggested < this.minPrice() ? row1.prices.suggested : this.minPrice());
        this.maxPrice.set(row1.prices.suggested > this.maxPrice() ? row1.prices.suggested : this.maxPrice());

        const currentDay = row1.quota.quantity * 15;
        availableDays.push(currentDay);

        if (!this.prices().includes(row1.prices.suggested)) {
          this.prices.update((prices: any) => [...prices, row1.prices.suggested]);
        }

        if (row1.prices.suggested == this.maxPrice()) {
          this.selectedProduct = row;
        }
      });
    });

    const uniqueDays = availableDays
      .filter((item, index) => availableDays.indexOf(item) === index)
      .sort((a, b) => Number(a) - Number(b));

    this.minDay.set(uniqueDays[0]);
    this.maxDay.set(uniqueDays[uniqueDays.length - 1]);
    this.selectedPrice = this.maxPrice();
    this.selectedDay = this.maxDay();
    this.onExtractDaysForSelected();

    this.autoSimulate();
    this.utilService.disablePageLoading();
  }

  /**
   * Extrae las opciones de días disponibles
   */
  onExtractDaysForSelected(): void {
    const min = this.minDay();
    const max = this.maxDay();
    this.dayOptions = [];

    for (let i = min; i <= max; i += 60) {
      this.dayOptions.push(i);
    }
  }

  /**
   * Ejecuta la simulación de crédito
   */
  simulate(): void {
    this.isSubmitted.set(true);
    if (this.form.invalid) {
      return;
    }

    this.form.patchValue({
      amount: this.selectedPrice,
      quota_quantity: this.selectedDay / 15,
      frecuency: 15
    });

    this.categoryRequestService.simulateCredit(this.requestService.categoryId, this.form.value).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.simulationsResults.set(resp.body.data);
          this.step = SimulatorSteps.results;
          this.isSubmitted.set(false);
        } else {
          this.customMessageService.responseError();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseError();
      }
    });
  }

  /**
   * Auto-simulación cuando cambian los parámetros
   */
  autoSimulate(): void {
    this.form.patchValue({
      amount: this.selectedPrice,
      quota_quantity: this.selectedDay / 15,
      frecuency: 15
    });

    this.categoryRequestService.simulateCredit(this.requestService.categoryId, this.form.value).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.simulationsResults.set(resp.body.data);
        }
      },
      error: () => {
        // Silently handle errors during auto-simulation
      }
    });
  }

  /**
   * Maneja el evento de slide del precio
   */
  onSlideEnd(price: any): void {
    this.onParameterChange(price);
  }

  /**
   * Maneja cambios en los parámetros del simulador
   */
  onParameterChange(price?: any): void {
    if (price !== undefined) {
      if (!this.prices().includes(price)) {
        let previousPrice = this.selectedProduct.quotes[0].prices.suggested;
        let priceTemp: any;

        if (price < previousPrice) {
          this.prices().sort((a: any, b: any) => b - a).forEach((row: any) => {
            if (row < price && priceTemp == undefined) {
              priceTemp = row;
            }
          });
        } else {
          this.prices().sort((a: any, b: any) => a - b).forEach((row: any) => {
            if (row > price && priceTemp == undefined) {
              priceTemp = row;
            }
          });
        }

        if (priceTemp) {
          this.selectedPrice = priceTemp;
        }
      }
      this.selectedProduct = this.getProductByPrice(this.selectedPrice);
    }
    this.autoSimulate();
  }

  /**
   * Maneja cambio de días seleccionados
   */
  onDayChange(day: number): void {
    this.selectedDay = day;
    this.autoSimulate();
  }

  /**
   * Obtiene el producto por precio
   */
  getProductByPrice(price: number): any {
    let product = null;
    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        if (row1.prices.suggested === price) {
          row.quotes.sort((a: any, b: any) => a.quota.quantity - b.quota.quantity);
          product = row;
        }
      });
    });
    return product;
  }

  /**
   * Continúa al siguiente paso
   */
  onContinue(): void {
    if (this.simulationsResults() && this.simulationsResults().quota_from) {
      this.onSave();
    } else {
      this.simulate();
    }
  }

  /**
   * Guarda la simulación y avanza
   */
  onSave(): void {
    this.metaPixelService.setLoanData(this.selectedPrice, 15, this.selectedDay / 15);
    this.metaPixelService.trackEvent('InitiateCheckout');

    const simulationData = { ...this.form.value, ...this.simulationsResults() };
    this.localStorageService.set(LocalStorageKey.simulation, simulationData);

    // En el flujo app-krdya-kredicash, persistir la simulación al backend
    // ya que no hay paso intermedio (client-identification) que lo haga
    const requestId = this.route.snapshot.params['request_id'];
    if (this.isFlowKrdya && requestId) {
      this.requestService.update(requestId, { simulation: simulationData }).subscribe({
        next: () => this.onNextStep(),
        error: () => this.onNextStep()
      });
    } else {
      this.onNextStep();
    }
  }

  /**
   * Regresa al paso anterior
   */
  onPreviousStep(step: string): void {
    this.step = SimulatorSteps[step as SimulatorSteps];
  }

  /**
   * Emite evento para avanzar al siguiente paso del workflow
   */
  onNextStep(): void {
    this.nextStepEvent.emit();
  }

  /**
   * Getter para acceder a los controles del formulario
   */
  get f() {
    return this.form.controls;
  }

  protected readonly SimulatorSteps = SimulatorSteps;
}
